import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import { useTheme } from '@zenbusiness/zui/styles';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';

import AddCounterPartyModal from '#client/components/AddCounterPartyModal';
import FormStepModal from '#client/components/FormStepModal';
import {
  COUNTERPARTY_ID,
  KEY_PARAM_MODAL,
  MFA_VERIFIED,
  MODAL_NAMES
} from '#client/constants/modals';
import { TOKEN_ATTRIBUTES } from '#client/constants/tokens';
import {
  WHICH_ACCOUNT_STEP,
  HOW_MUCH_STEP,
  TRANSFER_TYPE,
  MOVE_MONEY_STEPS_WITHOUT_CHOOSING_TYPE,
  IS_THAT_CORRECT_WIRE_STEP,
  CONFIRMATION_FORM_STEP
} from '#client/constants/transfer';
import useBankingPath from '#client/hooks/useBankingPath';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import { FormStepProvider } from '#client/hooks/useFormStep';
import useModalContext from '#client/hooks/useModalContext';
import usePlaid from '#client/hooks/usePlaid';
import useSessionStorage from '#client/hooks/useSessionStorage';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { extractTokenValue } from '#client/utils/token';

import CurrentStep from './components/CurrentStep';

export default function SendMoneyModal({
  counterParty = null,
  handleClose,
  steps = MOVE_MONEY_STEPS_WITHOUT_CHOOSING_TYPE,
  actionsKit = null,
  entryPageForSendMoney = null
}) {
  const { openModal, clearAllModal } = useModalContext();
  const { unitAccountId } = useUnitAccountId();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { accessToken } = useAuthentication();
  const [searchParams, setSearchParams] = useSearchParams();
  const bankingPath = useBankingPath();
  const theme = useTheme();
  const captureException = useCaptureException();
  const [counterPartyStorage, , , removeCounterPartyStorage] = useSessionStorage(
    'counterParty',
    {}
  );

  const [formData, setFormData] = useState({
    amount: 0,
    selectedToAccount: {},
    fromAccounts: [],
    toAccount: {},
    transferType: TRANSFER_TYPE.STANDARD_ACH.value,
    description: null,
    isResetCounterparties: false,
    payeeInfo: null,
    isDisableBack: false,
    confirmationTitle: '',
    dateInit: '',
    transactionId: null,
    idempotencyKey: null,
    entryPage: entryPageForSendMoney || `${bankingPath}/dashboard`
  });

  const closeHandler = () => {
    // In this step, the user close modal then we should clean local storage
    removeCounterPartyStorage();
    handleClose();
  };

  const [isStartWithWhichAccountStep] = useState(!counterParty);

  const retrieveTokenValue = useCallback(() => {
    try {
      return extractTokenValue(accessToken, TOKEN_ATTRIBUTES.IDEMPOTENCY_KEY);
    } catch (error) {
      captureException(error);
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const startStep = useMemo(() => {
    // Convert all search parameters to an array of with keys
    if (!isStartWithWhichAccountStep) {
      setFormData({
        ...formData,
        selectedToAccount: counterParty
      });

      return HOW_MUCH_STEP;
    }

    const hasCounterpartyId = searchParams.get(COUNTERPARTY_ID);
    const hasModalname = searchParams.get(KEY_PARAM_MODAL);
    if (hasCounterpartyId && hasModalname === MODAL_NAMES.SEND_MONEY) {
      return HOW_MUCH_STEP;
    }
    // Should open IS_THAT_CORRECT_WIRE_STEP if the URL callback
    // have the idempotencyKeyFromToken after MFA successfully
    const hasIdempotencyKey = Boolean(
      counterPartyStorage &&
      Object.keys(counterPartyStorage).length &&
      retrieveTokenValue() === counterPartyStorage?.idempotencyKey
    );
    const mfaVerified = searchParams.get(MFA_VERIFIED);
    if (hasIdempotencyKey && mfaVerified) {
      return IS_THAT_CORRECT_WIRE_STEP;
    }
    removeCounterPartyStorage();

    return WHICH_ACCOUNT_STEP;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchParams,
    counterParty,
    retrieveTokenValue,
    counterPartyStorage,
    isStartWithWhichAccountStep
  ]);

  const [currentFormStep, setCurrentFormStep] = useState(startStep);

  const handleBack = () => {
    const currentStepIndex = steps.indexOf(currentFormStep);
    const isClose =
      (currentFormStep === HOW_MUCH_STEP && !isStartWithWhichAccountStep) || currentStepIndex <= 0;
    // Using for back on HOW_MUCH_STEP incase has counterpartyId in URL
    const counterpartyId = searchParams.get(COUNTERPARTY_ID);
    if (currentFormStep === HOW_MUCH_STEP && counterpartyId) {
      setSearchParams(
        (prevParams) => {
          const newParams = new URLSearchParams(prevParams);
          newParams.delete(COUNTERPARTY_ID);
          return newParams;
        },
        { replace: true }
      );
    }
    if (isClose) {
      closeHandler();
      return;
    }

    setCurrentFormStep(steps[currentStepIndex - 1]);
  };

  const resetSearchParameters = () => {
    if (typeof actionsKit?.resetSearchParameters === 'function') {
      actionsKit.resetSearchParameters();
    }
  };

  const handleCloseModal = () => {
    closeHandler();
    clearAllModal();
    resetSearchParameters();
  };

  const onCounterPartyReceived = () => {
    setFormData({
      ...formData,
      isResetCounterparties: true
    });
  };

  const onCounterPartyCreatonError = () => {
    setFormData({
      ...formData,
      // True that mean the counter party list should be re-fetch (reset pagination)
      hasError: true
    });
  };

  const { isHandling } = usePlaid({
    unitAccountId,
    businessEntityUuid,
    counterPartyFields: ['id', 'bank', 'accountNumber', 'status'],
    onCounterPartyReceived,
    onCounterPartyCreatonError
  });

  // back to WHICH_ACCOUNT_STEP
  useEffect(() => {
    const isBackToWhichAccountStep = isHandling && currentFormStep === HOW_MUCH_STEP;
    if (isBackToWhichAccountStep) {
      setCurrentFormStep(WHICH_ACCOUNT_STEP);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHandling]);

  // we need to check currentFormStep === IS_THAT_CORRECT_WIRE_STEP because
  // IS_THAT_CORRECT_WIRE_STEP will be open via callback (get data from localStorage + from API)
  // so we need disable the back icon immediately before the data is loaded
  const disableBack =
    formData.isDisableBack ||
    [IS_THAT_CORRECT_WIRE_STEP, CONFIRMATION_FORM_STEP].includes(currentFormStep);

  const handleOpenAddPayeeModal = () => {
    openModal({
      component: AddCounterPartyModal,
      mode: AddCounterPartyModal.MODAL_TYPES.ADD_PAYEE,
      expectRedirectTo: `${bankingPath}/payees`,
      name: MODAL_NAMES.PAYEES_MODAL
    });
  };

  return (
    <FormStepModal
      disableBack={disableBack}
      handleBack={handleBack}
      handleClose={handleCloseModal}
      title="Send Money"
      titleBackground={theme.palette.background.grey.main}
    >
      <FormStepProvider
        value={{
          currentFormStep,
          setCurrentFormStep,
          data: formData,
          setData: setFormData
        }}
      >
        <CurrentStep
          currentFormStep={currentFormStep}
          handleBack={handleBack}
          handleClose={closeHandler}
          idempotencyKeyFromToken={retrieveTokenValue()}
          isHandling={isHandling}
          openAddPayeeModal={handleOpenAddPayeeModal}
        />
      </FormStepProvider>
    </FormStepModal>
  );
}

SendMoneyModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  counterParty: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    accountNumber: PropTypes.string,
    fullAccountNumber: PropTypes.string,
    wire: PropTypes.shape({
      address: PropTypes.shape({
        city: PropTypes.string,
        country: PropTypes.string,
        postalCode: PropTypes.string,
        state: PropTypes.string,
        street: PropTypes.string,
        street2: PropTypes.string
      }),
      routingNumber: PropTypes.string
    })
  }),
  steps: PropTypes.arrayOf(PropTypes.string),
  actionsKit: PropTypes.shape({
    resetSearchParameters: PropTypes.func
  }),
  entryPageForSendMoney: PropTypes.string
};
